import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { validateOptInToken, confirmOptIn } from '../api';
import '../index.css';

const OptInConfirmation = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [lead, setLead] = useState(null);
    const [formData, setFormData] = useState({
        postleitzahl: '',
        datenschutzAccepted: false
    });
    const [submitting, setSubmitting] = useState(false);

    const token = searchParams.get('token');

    useEffect(() => {
        if (!token) {
            setError('Kein Token gefunden');
            setLoading(false);
            return;
        }

        validateToken();
    }, [token]);

    const validateToken = async () => {
        try {
            const data = await validateOptInToken(token);

            if (data.success) {
                setLead(data.lead);
                setFormData({
                    postleitzahl: '',
                    datenschutzAccepted: false
                });
            } else {
                setError(data.error || 'Token ist ungültig');
            }
        } catch (err) {
            setError('Fehler beim Validieren des Tokens');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.datenschutzAccepted) {
            setError('Bitte bestätigen Sie die Datenschutzerklärung');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            const data = await confirmOptIn(
                token,
                formData.postleitzahl,
                formData.datenschutzAccepted
            );

            if (data.success) {
                // Weiterleitungsziel je nach Firma (Org/Agentur); Fallback BEN
                const redirectUrl = lead?.branding?.redirect_url
                    || (lead?.organization_id === 6 ? 'https://energie-plus.de' : 'https://www.bundesenergie-nord.de');
                window.location.href = redirectUrl;

            } else {
                setError(data.error || 'Fehler bei der Bestätigung');
            }
        } catch (err) {
            setError('Fehler bei der Bestätigung');
        } finally {
            setSubmitting(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="text-center">
                        <h2 className="text-3xl font-extrabold text-gray-900">
                            Opt-In Bestätigung
                        </h2>
                        <p className="mt-2 text-sm text-gray-600">
                            Token wird validiert...
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (error && !lead) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="text-center">
                        <h2 className="text-3xl font-extrabold text-gray-900">
                            Opt-In Bestätigung
                        </h2>
                        <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
                            <p className="text-sm text-red-600">{error}</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Branding kommt vom Backend (Org bzw. Agentur). Fallback auf Org-Logik für alte Antworten.
    const branding = lead?.branding || {
        heading_name: lead?.organization_id === 6 ? 'Energie Plus' : 'Bundesenergie Nord',
        company_name: lead?.organization_id === 6 ? 'Energie Plus GmbH' : 'Bundesenergie Nord BEN GmbH',
        datenschutz_url: lead?.organization_id === 6 ? 'https://www.energie-plus.de/datenschutz/' : 'https://www.bundesenergie-nord.de/datenschutz/',
        logo_url: lead?.organization_id === 6 ? '/logo_6.png' : '/logo_4.png',
        accent_color: '#4a6de5'
    };

    return (
        <div style={{ maxWidth: 600, margin: '40px auto', background: 'white', borderRadius: 8, boxShadow: '0 2px 12px #0001', padding: 32 }}>
            {branding.logo_url && (
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                    <img
                        src={branding.logo_url}
                        alt={`${branding.heading_name} Logo`}
                        style={{ maxWidth: 200, height: 'auto' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                </div>
            )}
            <h2 style={{ fontSize: 32, fontWeight: 700, textAlign: 'center', marginBottom: 24 }}>
                Opt in Bestätigung {branding.heading_name}
            </h2>
            {error && (
                <div style={{ color: 'red', marginBottom: 16, textAlign: 'center' }}>{error}</div>
            )}
            <form onSubmit={handleSubmit} style={{ maxWidth: 480, margin: '0 auto' }}>
                <div style={{ marginBottom: 24 }}>
                    <label htmlFor="postleitzahl" style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>
                        Postleitzahl*
                    </label>
                    <input
                        id="postleitzahl"
                        name="postleitzahl"
                        type="text"
                        required
                        value={formData.postleitzahl}
                        onChange={handleInputChange}
                        style={{ width: '100%', padding: 12, borderRadius: 4, border: '1px solid #ddd', background: '#f9fafb', fontSize: 16 }}
                        placeholder="Postleitzahl"
                    />
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 24 }}>
                    <input
                        id="datenschutzAccepted"
                        name="datenschutzAccepted"
                        type="checkbox"
                        required
                        checked={formData.datenschutzAccepted}
                        onChange={handleInputChange}
                        style={{ minWidth: 20, minHeight: 20, marginTop: 2, marginRight: 10 }}
                    />
                    <label htmlFor="datenschutzAccepted" style={{ fontWeight: 500, fontSize: 15, color: '#222' }}>
                        Bestätigung der Einwilligung zur telefonischen oder persönlichen Kontaktaufnahme durch {branding.company_name} oder deren Vertragspartner zur Datenerhebung, -verarbeitung und Informationszusendung. Die Datenschutzinformationen der {branding.company_name} sind hier einzusehen: <a href={branding.datenschutz_url} target="_blank" rel="noopener noreferrer" style={{ color: '#1d72b8', wordBreak: 'break-all' }}>{branding.datenschutz_url}</a> <span style={{ color: 'red' }}>*</span>
                    </label>
                </div>
                <button
                    type="submit"
                    disabled={submitting}
                    style={{ width: '100%', background: '#22c55e', color: 'white', padding: 14, border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 18, cursor: 'pointer', marginTop: 16 }}
                >
                    {submitting ? 'Wird bestätigt...' : 'Absenden'}
                </button>
            </form>
        </div>
    );
};

export default OptInConfirmation; 